import React, {useEffect, useState} from "react"
import {useDispatch} from "react-redux"
import {useLocation, useNavigate} from "react-router-dom"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import AddIcon from "@mui/icons-material/Add"
import DeleteIcon from "@mui/icons-material/Delete"
import DataRow from "@/components/ui/DataRow"
import Page from "@/layouts/Page"
import {Box, TextField} from "@mui/material"
import InputLabel from "@mui/material/InputLabel"
import MenuItem from "@mui/material/MenuItem"
import FormControl from "@mui/material/FormControl"
import Select, {SelectChangeEvent} from "@mui/material/Select"
import Chip from "@mui/material/Chip"
import Alert from "@mui/material/Alert"
import {getCondoLinks} from "@/api/condo"
import {condosRtkApi} from "@/api/condo"
import {useAuthContext} from "@/hooks/useAuthContext"
import {editInmueble} from "@/api/properties"
import {checkNameTaken} from "@/api/utils"
import {getAssociates} from "@/api/asociados"
import {normalizeSeaviewValue, prepareSeaviewForStorage, type SeaviewOption} from "@/types/Seaview"
import {OpportunityTag} from "@casas-diamante/types"
import {
   environments as envs,
   furnishTypes,
   PropertyStatus as Status,
   PropertyType,
   RegionCode,
   isHouseLikePropertyType,
   regions,
   zonesByRegion,
} from "@casas-diamante/types"
import DeletePropertyModal from "./DeleteModal"
import BooleanToggle from "@/components/properties/BooleanToggle"
import {clearCache, displayZoneLabel} from "@/utils"
import AssociatesSelect from "@/components/properties/AssociatesSelect"
import {useVideoList} from "@/hooks/useVideoList"
import {normalizeInitialVideos} from "@/video"

interface Condominio {
   value: string
   label: string
}

interface Partners {
   value: string
   label: string
}

function EditInmueble() {
   const location = useLocation()
   const navigate = useNavigate()
   const dispatch = useDispatch()
   const {user} = useAuthContext()
   const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false)
   const [error, setError] = useState<boolean | string>(false)
   const [condominios, setCondominios] = useState<Condominio[]>()
   // Inmueble
   const [prevName, setPrevName] = useState<null | string>(null)
   const [name, setName] = useState<null | string>(null)
   const [region, setRegion] = useState<RegionCode>(RegionCode.ACA)
   const [status, setStatus] = useState<null | Status>(null)
   const [desc, setDesc] = useState<null | string>(null)
   const [desc2, setDesc2] = useState<null | string>(null)
   const [urlCAD, setUrlCad] = useState<null | string>(null)
   const [condo, setCondo] = useState<null | string>(null)
   const [type, setType] = useState<null | PropertyType>(null)
   const [capacity, setCapacity] = useState<null | number>(null)
   const [mts, setMts] = useState<null | number>(null)
   const [mtsContruccion, setMtsContruccion] = useState<null | number>(null)
   const [floor, setFloor] = useState<null | number>(null)
   const [levels, setLevels] = useState<null | number>(null)
   const [rooms, setRooms] = useState<null | number>(null)
   const [bathrooms, setBathrooms] = useState<null | number>(null)
   const [parkingLots, setParkingLots] = useState<null | number>(null)
   const [mensual_anual, setMensualAnual] = useState<null | number>(null)
   const [mensual_regular, setMensualRegular] = useState<null | number>(null)
   const [noche_alta, setNocheAlta] = useState<null | number>(null)
   const [noche_media, setNocheMedia] = useState<null | number>(null)
   const [noche_regular, setNocheRegular] = useState<null | number>(null)
   const [costoVenta, setCostoVenta] = useState<null | number>(null)
   const [seaview, setSeaview] = useState<SeaviewOption>(null)
   const [furnished, setFurnished] = useState<string>("")
   const [furnishType, setFurnishType] = useState<string>("")
   const [, setPricePerMeter] = useState<null | number>(null)
   const [maintenance, setMaintenace] = useState<null | number>(null)
   const [zone, setZone] = useState<string | null>("")
   const [environments, setEnvironments] = useState<string[]>([])
   const [outingToTheBeach, setOutingToTheBeach] = useState<string>("")
   const [houseAntiquity, setHouseAntiquity] = useState<null | string>(null)
   const [available, setAvailable] = useState<boolean | null>(null)
   const [suspended, setSuspended] = useState<boolean | null>(null)
   const [mainImage, setMainImage] = useState<string>("")
   const [images, setImages] = useState<any>()
   const [orderedSections, setOrderedSections] = useState<string[]>()
   const [notes, setNotes] = useState<null | string>(null)
   const [privateNotes, setPrivateNotes] = useState<null | string>(null)
   const [ownerNotes, setOwnerNotes] = useState<null | string>(null)
   const [partners, setPartners] = useState<string[]>([])
   const [partnersFetched, setPartnersFetched] = useState<Partners[]>([])
   const [address, setAddress] = useState<null | string>(null)
   const [mapsUrl, setMapsUrl] = useState<string>("")
   const [mapsShareUrl, setMapsShareUrl] = useState<string>("")
   const [calendar, setCalendar] = useState<any>(null)
   const [verifiedAt, setVerifiedAt] = useState<null | string>(null)
   const [petFriendly, setPetFriendly] = useState<string>("")
   const [opportunityTag, setPriceTag] = useState<OpportunityTag | null>(null)
   //Files state
   const [selectedPublicFiles] = useState<File[]>([])
   const [selectedPrivateFiles] = useState<File[]>([])
   // loading state
   const [loading, setLoading] = useState<boolean>(false)
   const [loadingSubmit, setLoadingSubmit] = useState<boolean>(false)
   const [condoName, setCondoName] = useState<string>("")
   const [saleComission, setSaleComission] = useState<number | null>(null)
   const id = location.pathname.split("/editInmueble/")[1]
   const {videoInput, setVideoInput, videosList, addVideo, removeVideo, resetVideos} = useVideoList(
      {
         onError: setError,
      },
   )

   useEffect(() => {
      if (!error) {
         return
      }

      window.scrollTo({top: 0, behavior: "smooth"})
   }, [error])

   useEffect(() => {
      setLoading(true)
      const fetchPartners = async () => {
         const res = await getAssociates()
         const associates: Partners[] = []
         res.forEach((ass: any) => {
            associates.push({value: ass.id, label: ass.name})
         })
         setPartnersFetched(associates)
      }
      const state: any = location.state
      const inmueble: any = state.inmueble
      if (!inmueble) {
         navigate("/inmuebles")
         return
      }
      setName(inmueble.name)
      setRegion(inmueble.region)
      setPrevName(inmueble.name)
      setZone(inmueble.zone || "")
      setDesc(inmueble.desc)
      setDesc2(inmueble.desc2)
      setUrlCad(inmueble.urlCAD)
      setCalendar(inmueble.calendar || null)
      setCondo(inmueble.condo)
      setType(inmueble.type)
      setCapacity(inmueble.capacity)
      setMts(inmueble.mts)
      setVerifiedAt(inmueble.verifiedAt || null)
      setMtsContruccion(inmueble.mtsConstruction)
      setFloor(inmueble.floor)
      setLevels(inmueble.levels)
      setRooms(inmueble.rooms)
      setBathrooms(inmueble.bathrooms)
      setParkingLots(inmueble.parkingLots)
      setMensualAnual(inmueble.costos?.mensual_anual || null)
      setMensualRegular(inmueble.costos?.mensual_regular || null)
      setNocheAlta(inmueble.costos?.noche_alta || null)
      setNocheMedia(inmueble.costos?.noche_media || null)
      setNocheRegular(inmueble.costos?.noche_regular || null)
      setCostoVenta(inmueble?.costoVenta || null)
      setSeaview(normalizeSeaviewValue(inmueble.seaview))
      setFurnished(inmueble.furnished === true ? "true" : "false")
      setPricePerMeter(inmueble.pricePerMeter)
      setMaintenace(inmueble.maintenance)
      setStatus(inmueble.status)
      setEnvironments(inmueble.environments || [])
      setOutingToTheBeach(inmueble.outing_to_the_beach === true ? "true" : "false")
      setHouseAntiquity(inmueble.house_antiquity ? inmueble.house_antiquity : null)
      resetVideos(normalizeInitialVideos(inmueble))
      setMainImage(inmueble.mainImage)
      setImages(inmueble.images)
      setOrderedSections(inmueble.orderedSections)
      if (inmueble.available === undefined) {
         setLoading(false)
         return
      }
      setAvailable(inmueble.available)
      setSuspended(inmueble.suspended)
      setNotes(inmueble.notes)
      setOwnerNotes(inmueble.ownerNotes)
      setPartners(inmueble.partners || [])
      setAddress(inmueble.address)
      setFurnishType(inmueble.furnishType)
      setPrivateNotes(inmueble.privateNotes)
      setMapsUrl(inmueble.mapsUrl || "")
      setMapsShareUrl(inmueble.mapsShareUrl || "")
      setPetFriendly(
         inmueble.petFriendly !== undefined ? (inmueble.petFriendly ? "true" : "false") : "",
      )
      setCondoName(inmueble.condoName || "")
      setSaleComission(inmueble.saleComission || null)
      setPriceTag((inmueble.opportunityTag as OpportunityTag) ?? null)
      fetchPartners()
      setLoading(false)
   }, [])

   useEffect(() => {
      const fetchCondos = async () => {
         const res = await getCondoLinks(region)
         const condOptions: Condominio[] = []
         res.forEach((cond: any) => {
            condOptions.push({value: cond.id, label: cond.name})
         })
         setCondominios(condOptions)
      }
      fetchCondos()
   }, [region])

   // Boolean values for easier input validation
   const isHouse = isHouseLikePropertyType(type)
   const isApartment = type === PropertyType.Apartment
   const isLand = type === PropertyType.Land
   const isCommercial = type === PropertyType.Commercial
   const acapulcoSelected = region === RegionCode.ACA
   const saleArea = isLand ? mts : mtsContruccion

   // in order to update the enviroment state, which is an string array
   const handleChangeEnvironments = (event: SelectChangeEvent<typeof environments>) => {
      const {
         target: {value},
      } = event
      setEnvironments(
         // On autofill we get a stringified value.
         typeof value === "string" ? value.split(",") : value,
      )
   }

   const handleChangePartners = (event: SelectChangeEvent<(string | number)[]>) => {
      const {
         target: {value},
      } = event
      setPartners(
         // On autofill we get a stringified value.
         typeof value === "string" ? value.split(",") : value.map((v) => v.toString()),
      )
   }

   const handleSubmit = async () => {
      setLoadingSubmit(true)
      setError(false)
      // check for every required field to be filled
      // if not an error is set
      if (!name || name === "") {
         setLoadingSubmit(false)
         setError("Elija un nombre")
         return
      }
      if (name !== prevName) {
         const nameTaken = await checkNameTaken(name, "inmuebles")
         if (nameTaken) {
            setLoadingSubmit(false)
            setError(
               "El nombre de este inmueble ya está tomado. Favor de especificar otro nombre o contactar al administrador para notificar de un problema.",
            )
            return
         }
      }
      if (isApartment && (!condo || condo === "")) {
         setLoading(false)
         setError("Elija un condominio")
         return
      }
      if (acapulcoSelected && !seaview) {
         setLoadingSubmit(false)
         setError("Elija si tiene vista al mar")
         return
      }
      if (!condo && zone === "") {
         setLoadingSubmit(false)
         setError("Elija una zona porque no hay condominio asignado")
         return
      }
      if (isHouse && mapsShareUrl === "") {
         setLoadingSubmit(false)
         setError("Como es casa o especial, ingrese la URL de Google Maps para compartir")
         return
      }
      const saleArea = isLand ? mts : mtsContruccion
      const inmuebleUpdated: any = {
         id,
         name,
         status,
         region,
         desc: desc === "" ? null : desc?.trim(),
         desc2: desc2 === "" ? null : desc2?.trim(),
         urlCAD: urlCAD === "" ? null : urlCAD,
         condo: condo,
         type,
         capacity: capacity === 0 ? null : capacity,
         mts: mts === 0 ? null : mts,
         mtsConstruction: mtsContruccion === 0 ? null : mtsContruccion,
         floor: floor === 0 || isHouse ? null : floor,
         levels: levels === 0 ? null : levels,
         rooms: rooms === 0 ? null : rooms,
         bathrooms: bathrooms === 0 ? null : bathrooms,
         parkingLots: parkingLots === 0 ? null : parkingLots,
         costos: {
            mensual_anual: mensual_anual === 0 ? null : mensual_anual,
            mensual_regular: mensual_regular === 0 ? null : mensual_regular,
            noche_alta: noche_alta === 0 ? null : noche_alta,
            noche_media: noche_media === 0 ? null : noche_media,
            noche_regular: noche_regular === 0 ? null : noche_regular,
         },
         costoVenta: costoVenta === 0 ? null : costoVenta,
         videos: videosList.length <= 0 ? null : videosList,
         seaview: acapulcoSelected ? prepareSeaviewForStorage(seaview) : null,
         publicFiles: selectedPublicFiles.length <= 0 ? null : selectedPublicFiles,
         privateFiles: selectedPrivateFiles.length <= 0 ? null : selectedPrivateFiles,
         furnished: isLand ? null : furnished === "true" ? true : false,
         pricePerMeter:
            saleArea === 0 || !saleArea || costoVenta === 0 || !costoVenta
               ? null
               : costoVenta / saleArea,
         maintenance: maintenance === 0 ? null : maintenance,
         environments: environments.length > 0 ? environments : null,
         outing_to_the_beach: acapulcoSelected
            ? outingToTheBeach === "true"
               ? true
               : false
            : null,
         house_antiquity: houseAntiquity === "" || !isHouse ? null : houseAntiquity,
         available: available,
         suspended: suspended,
         mainImage: mainImage,
         images: images,
         orderedSections: orderedSections,
         verifiedAt: verifiedAt,
         calendar: calendar,
         notes: notes === "" ? null : notes,
         privateNotes: privateNotes === "" ? null : privateNotes,
         ownerNotes: ownerNotes === "" ? null : ownerNotes,
         partners: partners.length <= 0 ? null : partners,
         address: address === "" ? null : address,
         furnishType: furnishType === "" ? null : furnishType,
         mapsUrl: mapsUrl === "" ? null : mapsUrl,
         mapsShareUrl: mapsShareUrl === "" ? null : mapsShareUrl,
         zone: zone === "" ? null : zone,
         forSale: costoVenta ? true : false,
         // For rent is set after costos is parsed
         petFriendly: petFriendly === "" ? null : petFriendly === "true" ? true : false,
         condoName: condoName === "" ? null : condoName,
         saleComission: saleComission ? saleComission : null,
         opportunityTag: opportunityTag,
      }

      const inmuebleFields = Object.keys(inmuebleUpdated)
      for (const key in inmuebleFields) {
         if (
            (inmuebleUpdated[inmuebleFields[key]] === null ||
               inmuebleUpdated[inmuebleFields[key]] === undefined) &&
            inmuebleFields[key] !== "mainImage"
         ) {
            delete inmuebleUpdated[inmuebleFields[key]]
         }
      }
      const costosFields = Object.keys(inmuebleUpdated.costos)
      for (const key in costosFields) {
         if (
            inmuebleUpdated.costos[costosFields[key]] === null ||
            inmuebleUpdated.costos[costosFields[key]] === undefined
         ) {
            delete inmuebleUpdated.costos[costosFields[key]]
         }
      }
      const newCostos = Object.keys(inmuebleUpdated.costos)
      if (newCostos.length <= 0) {
         delete inmuebleUpdated.costos
      }

      // Set updated property's forRent value
      if (inmuebleUpdated.costos) {
         inmuebleUpdated.forRent = true
      } else {
         inmuebleUpdated.forRent = false
      }

      try {
         await editInmueble(inmuebleUpdated)
         dispatch(condosRtkApi.util.invalidateTags([{type: "CondoPriceAnalysis", id: "LIST"}]))
         navigate(`/inmuebles/${id}`)
         clearCache(["properties"])
         setLoadingSubmit(false)
      } catch (error) {
         setLoadingSubmit(false)
         setError(
            error.response.data.errors[0].msg
               ? error.response.data.errors[0].msg
               : "Error al editar el inmueble",
         )
         console.log(error)
      }
   }

   return !loading ? (
      <Page>
         {/* if clicked you are returned to the latest page */}
         <header>
            <ArrowBackIcon
               className="font-bold mb-7 cursor-pointer mt-5"
               onClick={() => navigate(-1)}
            />
         </header>
         {/* in this div all our inputs are shown */}
         <div className="mb-8 flex flex-col max-w-[1600px] mx-auto px-4 md:px-[5%] gap-4">
            {/*
         if the showImageForm state is false, then the normal fields are displayed, 
         if not the image fields are displayed */}
            {/* title */}
            <h3 className="text-center text-2xl text-blue-600 font-bold mt-5 mb-5">
               Editar: {name}
            </h3>
            {/* error alert */}
            {error && (
               <Alert
                  severity="error"
                  className="text-center text-2xl mb-5 w-full md:w-[30%] mx-auto"
               >
                  {error}
               </Alert>
            )}
            <h3 className="text-blue mb-2">Información general</h3>
            <DataRow>
               <TextField
                  size="small"
                  label="Nombre de propiedad *"
                  fullWidth
                  value={name ? name : ""}
                  onChange={(e) => setName(e.target.value)}
               />
               <FormControl size="small" fullWidth sx={{mx: {xs: 0, md: "2%"}, mt: {xs: 1, md: 0}}}>
                  <InputLabel>Región *</InputLabel>
                  <Select
                     label="Región *"
                     value={region}
                     onChange={(e) => setRegion(e.target.value as RegionCode)}
                     disabled
                  >
                     {regions.map((region) => (
                        <MenuItem key={region.code} value={region.code}>
                           {region.text}
                        </MenuItem>
                     ))}
                  </Select>
               </FormControl>
               <FormControl
                  size="small"
                  fullWidth
                  sx={{mx: {xs: 0, md: "2%"}, mt: {xs: 0.75, md: 0}}}
               >
                  <InputLabel id="demo-controlled-open-select-label">Zona</InputLabel>
                  <Select
                     labelId="demo-controlled-open-select-label"
                     id="demo-controlled-open-select"
                     label="Zona"
                     value={zone}
                     disabled={condo ? true : false}
                     onChange={(e) => setZone(e.target.value)}
                  >
                     {zonesByRegion[region]?.map((z) => (
                        <MenuItem value={z} key={z}>
                           {displayZoneLabel(z)}
                        </MenuItem>
                     ))}
                  </Select>
               </FormControl>
            </DataRow>
            <DataRow>
               <BooleanToggle
                  label={
                     suspended
                        ? "Apagado"
                        : suspended === undefined
                        ? "Sin actualizar"
                        : "Encendido"
                  }
                  onPress={() => {
                     const nextSuspended = suspended !== true
                     setSuspended(nextSuspended)
                     if (nextSuspended) setAvailable(false)
                  }}
                  condition={suspended === false}
               />
               <BooleanToggle
                  label={
                     available
                        ? "Disponible"
                        : available === undefined
                        ? "Sin actualizar"
                        : "No disponible"
                  }
                  onPress={() => setAvailable(available === true ? false : true)}
                  condition={available === true}
                  disabled={suspended === true}
               />
               <FormControl size="small">
                  <InputLabel shrink>Etiqueta de oportunidad</InputLabel>
                  <Select
                     displayEmpty
                     notched
                     value={opportunityTag ?? ""}
                     label="Etiqueta de oportunidad"
                     onChange={(e) =>
                        setPriceTag(
                           e.target.value === "" ? null : (e.target.value as OpportunityTag),
                        )
                     }
                  >
                     <MenuItem value="">Sin etiqueta</MenuItem>
                     <MenuItem value={OpportunityTag.Opportunity}>Oportunidad (15-25%)</MenuItem>
                     <MenuItem value={OpportunityTag.UniqueOpportunity}>
                        Oportunidad única (25%&lt;)
                     </MenuItem>
                  </Select>
               </FormControl>
               <Select
                  displayEmpty
                  value={status}
                  onChange={(e) => setStatus(e.target.value as Status)}
                  className="h-[38px]"
                  disabled={isLand}
               >
                  {Object.values(Status).map((s, i) => (
                     <MenuItem value={s} key={i}>
                        {s}
                     </MenuItem>
                  ))}
               </Select>
            </DataRow>
            <DataRow>
               <FormControl size="small" fullWidth sx={{mx: {xs: 0, md: "2%"}, mt: {xs: 1, md: 0}}}>
                  <InputLabel id="demo-controlled-open-select-label">
                     Tipo de propiedad *
                  </InputLabel>
                  <Select
                     displayEmpty
                     labelId="demo-controlled-open-select-label"
                     id="demo-controlled-open-select"
                     label="Tipo de propiedad *"
                     value={type ? type : ""}
                     disabled
                  >
                     {Object.values(PropertyType).map((t, i) => (
                        <MenuItem value={t} key={i}>
                           {t}
                        </MenuItem>
                     ))}
                  </Select>
               </FormControl>
               {/* condominio */}
               <FormControl size="small" fullWidth sx={{mx: {xs: 0, md: "2%"}, mt: {xs: 1, md: 0}}}>
                  <InputLabel id="demo-controlled-open-select-label">Condominio</InputLabel>
                  <Select
                     displayEmpty
                     labelId="demo-controlled-open-select-label"
                     id="demo-controlled-open-select"
                     label="Condominio"
                     value={condo ? condo : ""}
                     onChange={(e) => {
                        setCondo(e.target.value)
                        setZone("")
                     }}
                     disabled
                  >
                     {condominios?.map((cond) => (
                        <MenuItem key={cond.value} value={cond.value}>
                           {cond.label}
                        </MenuItem>
                     ))}
                  </Select>
               </FormControl>
               {/* rooms */}
               <TextField
                  size="small"
                  label="Recámaras *"
                  id="outlined-size-small"
                  fullWidth
                  sx={{mx: {xs: 0, md: "2%"}, mt: {xs: 1, md: 0}}}
                  value={rooms ? rooms : ""}
                  onChange={(e) => setRooms(+e.target.value)}
                  disabled={isLand || isCommercial}
                  inputProps={{inputMode: "numeric", pattern: "[0-9]*"}}
               />
               {/* bathrooms */}
               <FormControl
                  size="small"
                  fullWidth
                  disabled={isLand}
                  sx={{mx: {xs: 0, md: "2%"}, mt: {xs: 1, md: 0}}}
               >
                  <InputLabel>Baños *</InputLabel>
                  <Select
                     label="Baños *"
                     value={bathrooms ? bathrooms : ""}
                     onChange={(e) => setBathrooms(e.target.value as number)}
                  >
                     {Array.from({length: 49}, (_, i) => 1 + i * 0.5).map((val) => (
                        <MenuItem key={val} value={val}>
                           {val}
                        </MenuItem>
                     ))}
                  </Select>
               </FormControl>
               {/* parkingLots */}
               <TextField
                  size="small"
                  label="Cajones de estacionamiento"
                  id="outlined-size-small"
                  disabled={isLand}
                  fullWidth
                  sx={{mx: {xs: 0, md: "2%"}, mt: {xs: 1, md: 0}}}
                  value={parkingLots ? parkingLots : ""}
                  onChange={(e) => setParkingLots(+e.target.value)}
                  inputProps={{inputMode: "numeric", pattern: "[0-9]*"}}
               />
            </DataRow>
            <DataRow>
               <TextField
                  size="small"
                  label="Niveles"
                  fullWidth
                  disabled={isLand}
                  value={levels ? levels : ""}
                  onChange={(e) => setLevels(+e.target.value)}
                  inputProps={{inputMode: "numeric", pattern: "[0-9]*"}}
               />
               <TextField
                  size="small"
                  label="Piso"
                  disabled={isHouse || isLand}
                  fullWidth
                  value={floor ? floor : ""}
                  onChange={(e) => setFloor(+e.target.value)}
                  inputProps={{inputMode: "numeric", pattern: "[0-9]*"}}
               />
               <TextField
                  size="small"
                  label="Superficie terreno Mts²"
                  fullWidth
                  value={mts ? mts : ""}
                  onChange={(e) => setMts(+e.target.value)}
                  inputProps={{inputMode: "numeric", pattern: "[0-9]*"}}
                  disabled={isApartment}
               />
               <TextField
                  size="small"
                  label="Superficie construcción Mts²"
                  fullWidth
                  disabled={isLand}
                  value={mtsContruccion ? mtsContruccion : ""}
                  onChange={(e) => setMtsContruccion(+e.target.value)}
                  inputProps={{inputMode: "numeric", pattern: "[0-9]*"}}
               />
            </DataRow>
            <DataRow>
               <TextField
                  size="small"
                  label="Dirección"
                  fullWidth
                  value={address ? address : ""}
                  onChange={(e) => setAddress(e.target.value)}
               />
            </DataRow>
            <DataRow>
               <TextField
                  size="small"
                  label="URL CAD"
                  fullWidth
                  value={urlCAD ? urlCAD : ""}
                  onChange={(e) => setUrlCad(e.target.value)}
               />
               <TextField
                  size="small"
                  label="Google Maps URL para EMBED"
                  id="outlined-size-small"
                  fullWidth
                  sx={{mx: {xs: 0, md: "2%"}, mt: {xs: 1, md: 0}}}
                  value={mapsUrl}
                  onChange={(e) => setMapsUrl(e.target.value)}
               />
               <TextField
                  size="small"
                  label="Google Maps URL para Compartir"
                  id="outlined-size-small"
                  fullWidth
                  sx={{mx: {xs: 0, md: "2%"}, mt: {xs: 1, md: 0}}}
                  value={mapsShareUrl}
                  onChange={(e) => setMapsShareUrl(e.target.value)}
               />
            </DataRow>
            <AssociatesSelect
               value={partners}
               onChange={handleChangePartners}
               options={partnersFetched}
               showAllSelected
            />
            <h3>Ambientes y otros detalles</h3>
            <DataRow>
               <FormControl size="small" fullWidth sx={{mx: {xs: 0, md: "2%"}, mt: {xs: 1, md: 0}}}>
                  <InputLabel id="demo-controlled-open-select-label">Vista al mar *</InputLabel>
                  <Select
                     displayEmpty
                     id="demo-controlled-open-select"
                     label="Vista al mar *"
                     disabled={!acapulcoSelected}
                     value={seaview ?? ""}
                     onChange={(e) =>
                        setSeaview(
                           e.target.value === ""
                              ? null
                              : (e.target.value as Exclude<SeaviewOption, null>),
                        )
                     }
                  >
                     <MenuItem value={""} disabled>
                        <em>Seleccione una opción</em>
                     </MenuItem>
                     <MenuItem value={"yes"}>Si</MenuItem>
                     <MenuItem value={"no"}>No</MenuItem>
                     <MenuItem value={"partial"}>Vista parcial</MenuItem>
                  </Select>
               </FormControl>
               <FormControl size="small" fullWidth sx={{mx: {xs: 0, md: "2%"}, mt: {xs: 1, md: 0}}}>
                  <InputLabel id="demo-controlled-open-select-label">Amueblado *</InputLabel>
                  <Select
                     disabled={isLand}
                     displayEmpty
                     labelId="demo-controlled-open-select-label"
                     id="demo-controlled-open-select"
                     label="Amueblado *"
                     value={furnished}
                     onChange={(e) => {
                        const value = e.target.value
                        if (value === "true") {
                           setFurnished(value)
                           setFurnishType("Amueblado")
                        } else {
                           setFurnished(value)
                           setFurnishType("Sin muebles")
                        }
                     }}
                  >
                     <MenuItem value={"true"}>Si</MenuItem>
                     <MenuItem value={"false"}>No</MenuItem>
                  </Select>
               </FormControl>
               {/* type of furnish */}
               <FormControl size="small" fullWidth sx={{mx: {xs: 0, md: "2%"}, mt: {xs: 1, md: 0}}}>
                  <InputLabel id="demo-controlled-open-select-label">Tipo de amueblado</InputLabel>
                  <Select
                     displayEmpty
                     labelId="demo-controlled-open-select-label"
                     disabled={isLand}
                     id="demo-controlled-open-select"
                     label="Tipo de amueblado"
                     value={furnishType}
                     onChange={(e) => {
                        const value = e.target.value
                        if (value === "Amueblado" || value === "Mobiliario a tratar") {
                           setFurnished("true")
                           setFurnishType(value)
                        } else {
                           setFurnished("false")
                           setFurnishType(value)
                        }
                     }}
                  >
                     {furnishTypes.map((type: string) => (
                        <MenuItem value={type} key={type}>
                           {type}
                        </MenuItem>
                     ))}
                  </Select>
               </FormControl>
            </DataRow>
            {/* Ambientes */}
            <FormControl
               size="small"
               sx={{mx: {xs: 0, md: "2%"}, mt: {xs: 1, md: 0}, width: "100%"}}
            >
               <InputLabel id="demo-multiple-chip-label">Ambientes de la propiedad *</InputLabel>
               <Select
                  labelId="demo-multiple-chip-label"
                  id="demo-multiple-chip"
                  multiple
                  disabled={isLand || isCommercial}
                  value={environments}
                  onChange={handleChangeEnvironments}
                  label="Ambientes de la propiedad *"
                  renderValue={(selected) => (
                     <Box sx={{display: "flex", flexWrap: "wrap", gap: 0.5}}>
                        {selected.map((value) => (
                           <Chip key={value} label={value} />
                        ))}
                     </Box>
                  )}
               >
                  {envs.map((env) => (
                     <MenuItem key={env} value={env}>
                        {env}
                     </MenuItem>
                  ))}
               </Select>
            </FormControl>
            <FormControl size="small" fullWidth sx={{mx: {xs: 0, md: "2%"}, mt: {xs: 1, md: 0}}}>
               <InputLabel id="demo-controlled-open-select-label">Antigüedad de la casa</InputLabel>
               <Select
                  displayEmpty
                  disabled={!isHouse}
                  labelId="demo-controlled-open-select-label"
                  id="demo-controlled-open-select"
                  label="Antiguedad de la casa"
                  value={houseAntiquity ? houseAntiquity : ""}
                  onChange={(e) => setHouseAntiquity(e.target.value)}
               >
                  <MenuItem value={"En construcción"}>En construcción</MenuItem>
                  <MenuItem value={"A estrenar"}>A estrenar</MenuItem>
                  <MenuItem value={"Hasta 5 años"}>Hasta 5 años</MenuItem>
                  <MenuItem value={"Hasta 10 años"}>Hasta 10 años</MenuItem>
                  <MenuItem value={"Hasta 20 años"}>Hasta 20 años</MenuItem>
                  <MenuItem value={"Hasta 50 años"}>Hasta 50 años</MenuItem>
               </Select>
            </FormControl>
            <DataRow>
               <FormControl size="small" fullWidth sx={{mx: {xs: 0, md: "2%"}, mt: {xs: 1, md: 0}}}>
                  <InputLabel id="demo-controlled-open-select-label">Salida a la playa</InputLabel>
                  <Select
                     displayEmpty
                     disabled={!acapulcoSelected || isApartment || isCommercial}
                     labelId="demo-controlled-open-select-label"
                     id="demo-controlled-open-select"
                     label="Salida a la playa"
                     value={outingToTheBeach}
                     onChange={(e) => setOutingToTheBeach(e.target.value)}
                  >
                     <MenuItem value={"true"}>Si</MenuItem>
                     <MenuItem value={"false"}>No</MenuItem>
                  </Select>
               </FormControl>
            </DataRow>
            <h3>Información de Venta</h3>
            <DataRow>
               {/* costoVenta */}
               <TextField
                  size="small"
                  label="Precio de venta MXN"
                  id="outlined-size-small"
                  fullWidth
                  sx={{mx: {xs: 0, md: "2%"}, mt: {xs: 1, md: 0}}}
                  value={costoVenta ? costoVenta : ""}
                  onChange={(e) => setCostoVenta(+e.target.value)}
                  inputProps={{inputMode: "numeric", pattern: "[0-9]*"}}
               />
               {/* pricePerMeter */}
               <TextField
                  size="small"
                  label="Precio por metro cuadrado"
                  id="outlined-size-small"
                  fullWidth
                  sx={{mx: {xs: 0, md: "2%"}, mt: {xs: 1, md: 0}}}
                  value={costoVenta && saleArea ? (costoVenta / saleArea).toFixed(2) : ""}
                  inputProps={{inputMode: "numeric", pattern: "[0-9]*"}}
                  disabled
               />
               {/* maintenance */}
               <TextField
                  size="small"
                  label="Mantenimiento"
                  id="outlined-size-small"
                  fullWidth
                  sx={{mx: {xs: 0, md: "2%"}, mt: {xs: 1, md: 0}}}
                  value={maintenance ? maintenance : ""}
                  onChange={(e) => setMaintenace(+e.target.value)}
                  inputProps={{inputMode: "numeric", pattern: "[0-9]*"}}
               />
            </DataRow>
            <FormControl size="small">
               <InputLabel shrink>Comisión de Venta Pactada (%)</InputLabel>
               <Select
                  displayEmpty
                  label="Comisión de Venta Pactada (%)"
                  value={saleComission ?? ""} // si es null, que muestre la opción vacía
                  onChange={(e) => {
                     const val = e.target.value
                     setSaleComission(val === "" ? null : Number(val))
                  }}
               >
                  <MenuItem value="">
                     <em>Sin comisión</em>
                  </MenuItem>
                  {Array.from({length: 19}, (_, i) => i * 0.5 + 1).map((i) => (
                     <MenuItem key={i} value={i}>
                        {i}%
                     </MenuItem>
                  ))}
               </Select>
            </FormControl>
            <h3>Información de Renta</h3>
            <DataRow>
               {/* precio noche regular */}
               <TextField
                  size="small"
                  label="MXN Renta/noche temporada regular"
                  id="outlined-size-small"
                  fullWidth
                  sx={{mx: {xs: 0, md: "2%"}, mt: {xs: 1, md: 0}}}
                  disabled={isLand || isCommercial}
                  value={noche_regular ? noche_regular : ""}
                  onChange={(e) => setNocheRegular(+e.target.value)}
                  inputProps={{inputMode: "numeric", pattern: "[0-9]*"}}
               />
               {/* precio noche media */}
               <TextField
                  size="small"
                  label="MXN Renta/noche temporada media"
                  id="outlined-size-small"
                  fullWidth
                  sx={{mx: {xs: 0, md: "2%"}, mt: {xs: 1, md: 0}}}
                  disabled={isLand || isCommercial}
                  value={noche_media ? noche_media : ""}
                  onChange={(e) => setNocheMedia(+e.target.value)}
                  inputProps={{inputMode: "numeric", pattern: "[0-9]*"}}
               />
               {/* precio noche alta */}
               <TextField
                  size="small"
                  label="MXN Renta/noche temporada alta"
                  id="outlined-size-small"
                  fullWidth
                  disabled={isLand || isCommercial}
                  sx={{mx: {xs: 0, md: "2%"}, mt: {xs: 1, md: 0}}}
                  value={noche_alta ? noche_alta : ""}
                  onChange={(e) => setNocheAlta(+e.target.value)}
                  inputProps={{inputMode: "numeric", pattern: "[0-9]*"}}
               />
            </DataRow>
            <DataRow>
               {/* precio mensual regular */}
               <TextField
                  size="small"
                  label="MXN Renta/mes contrato regular"
                  id="outlined-size-small"
                  fullWidth
                  sx={{mx: {xs: 0, md: "2%"}, mt: {xs: 1, md: 0}}}
                  value={mensual_regular ? mensual_regular : ""}
                  onChange={(e) => setMensualRegular(+e.target.value)}
                  inputProps={{inputMode: "numeric", pattern: "[0-9]*"}}
               />
               {/* preio mensual */}
               <TextField
                  size="small"
                  label="MXN Renta/mes contrato anual"
                  id="outlined-size-small"
                  fullWidth
                  sx={{mx: {xs: 0, md: "2%"}, mt: {xs: 1, md: 0}}}
                  value={mensual_anual ? mensual_anual : ""}
                  onChange={(e) => setMensualAnual(+e.target.value)}
                  inputProps={{inputMode: "numeric", pattern: "[0-9]*"}}
               />
            </DataRow>
            <DataRow>
               {/* capacity */}
               <TextField
                  size="small"
                  label="Capacidad máxima"
                  id="outlined-size-small"
                  disabled={isLand || isCommercial}
                  fullWidth
                  sx={{mx: {xs: 0, md: "2%"}, mt: {xs: 1, md: 0}}}
                  value={capacity ? capacity : ""}
                  onChange={(e) => setCapacity(+e.target.value)}
                  inputProps={{inputMode: "numeric", pattern: "[0-9]*"}}
               />
               {/* Pet Friendly */}
               <FormControl size="small" fullWidth sx={{mx: {xs: 0, md: "2%"}, mt: {xs: 1, md: 0}}}>
                  <InputLabel id="demo-controlled-open-select-label">Pet Friendly</InputLabel>
                  <Select
                     displayEmpty
                     labelId="demo-controlled-open-select-label"
                     id="demo-controlled-open-select"
                     label="Pet Friendly"
                     disabled={isLand}
                     value={petFriendly}
                     onChange={(e) => setPetFriendly(e.target.value)}
                  >
                     <MenuItem value={"true"}>Sí</MenuItem>
                     <MenuItem value={"false"}>No</MenuItem>
                  </Select>
               </FormControl>
            </DataRow>
            <h3 className="text-blue">Descripción de la propiedad</h3>
            {/* description2 */}
            <TextField
               size="small"
               label="Descripción breve (ficha técnica)"
               id="outlined-size-small"
               fullWidth
               multiline
               sx={{mx: {xs: 0, md: "2%"}, mt: {xs: 1, md: 0}}}
               value={desc2 ? desc2 : ""}
               onChange={(e) => setDesc2(e.target.value)}
            />
            {/* description */}
            <TextField
               size="small"
               label="Descripción detallada"
               id="outlined-size-small"
               fullWidth
               multiline
               sx={{mx: {xs: 0, md: "2%"}, mt: {xs: 1, md: 0}}}
               value={desc ? desc : ""}
               onChange={(e) => setDesc(e.target.value)}
            />
            <h3 className="text-blue">Notas de la propiedad</h3>
            <h3>Notas públicas</h3>
            {/* public notes */}
            <TextField
               size="small"
               label="Notas públicas"
               id="outlined-size-small"
               fullWidth
               multiline
               sx={{mx: {xs: 0, md: "2%"}, mt: {xs: 1, md: 0}}}
               value={notes ? notes : ""}
               onChange={(e) => setNotes(e.target.value)}
            />
            <h3>Notas privadas</h3>
            {/* private notes */}
            <TextField
               size="small"
               label="Notas privadas"
               id="outlined-size-small"
               fullWidth
               multiline
               sx={{mx: {xs: 0, md: "2%"}, mt: {xs: 1, md: 0}}}
               value={privateNotes ? privateNotes : ""}
               onChange={(e) => setPrivateNotes(e.target.value)}
            />
            <h3>Notas al propietario</h3>
            <TextField
               size="small"
               label="Notas al propietario"
               id="owner-notes"
               fullWidth
               multiline
               sx={{mx: {xs: 0, md: "2%"}, mt: {xs: 1, md: 0}}}
               value={ownerNotes ? ownerNotes : ""}
               onChange={(e) => setOwnerNotes(e.target.value)}
            />
            <h3>Videos</h3>
            {/* video links */}
            <div className="w-full flex flex-col gap-4">
               <div className="w-full flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
                  <TextField
                     size="small"
                     label="Links de videos"
                     id="outlined-size-small"
                     fullWidth
                     sx={{mx: {xs: 0, md: "2%"}, mt: {xs: 1, md: 0}}}
                     value={videoInput}
                     onChange={(e) => setVideoInput(e.target.value)}
                  />
                  <AddIcon className="text-blue-600 self-start sm:self-center" onClick={addVideo} />
               </div>
               {videosList.length > 0 &&
                  videosList.map((video, index) => (
                     <div
                        key={video}
                        className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4 my-4 border rounded p-5"
                     >
                        <p className="w-[100%] overflow-x-scroll overflow-y-hidden">{video}</p>
                        <DeleteIcon
                           className="text-red-600 self-start sm:self-center"
                           onClick={() => removeVideo(index)}
                        />
                     </div>
                  ))}
            </div>
            {/* add button */}
            {!loadingSubmit && (
               <>
                  <button
                     className="rounded bg-green p-2 mb-5 text-white font-bold w-[200px] self-center"
                     onClick={handleSubmit}
                  >
                     Guardar Cambios
                  </button>
                  <button
                     className="rounded bg-red p-2 mb-5 mt-10 text-white font-bold w-[200px] self-center"
                     onClick={() => setShowDeleteModal(true)}
                  >
                     Eliminar propiedad
                  </button>
               </>
            )}
            {loadingSubmit && (
               <button
                  className="rounded bg-blue p-2 mb-5 text-white font-bold w-[200px] self-center"
                  disabled
               >
                  Cargando...
               </button>
            )}
         </div>
         <DeletePropertyModal
            isOpen={showDeleteModal}
            setIsOpen={setShowDeleteModal}
            userEmail={user.email}
            propertyId={id}
         />
      </Page>
   ) : (
      <Page>
         <div className="flex flex-col items-center justify-center h-screen">
            <h1 className="text-2xl font-bold">Cargando...</h1>
         </div>
      </Page>
   )
}

export default EditInmueble
